import { format } from 'date-fns-tz';
import { formatDistanceToNow } from 'date-fns';

export interface TimeOptions {
  showTimezone?: boolean;
  timezone?: string | null;
}

let defaultTimezoneOptions: TimeOptions = {};

export const setDefaultTimezoneOptions = (options: TimeOptions) => {
  defaultTimezoneOptions = options;
};

export const getDefaultTimezoneFormatOptions =
  (): Intl.DateTimeFormatOptions => {
    const { showTimezone = false, timezone } = defaultTimezoneOptions;
    return {
      timeZone: timezone || undefined,
      timeZoneName: showTimezone ? 'short' : undefined,
    };
  };

export const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

export const TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

export const formatDate = (
  date?: number | string | Date | null,
  formatStr?: string,
  options: TimeOptions = {},
): string => {
  if (!date) {
    return '';
  }
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  if (formatStr) {
    return format(dateObj, formatStr);
  }
  const { showTimezone = false, timezone = null } = {
    ...defaultTimezoneOptions,
    ...options,
  };
  return new Intl.DateTimeFormat('en-US', {
    ...DATE_FORMAT_OPTIONS,
    timeZone: timezone || undefined,
    timeZoneName: showTimezone ? 'short' : undefined,
  }).format(dateObj);
};

export const formatDatetime = (
  date?: number | string | Date | null,
  formatStr?: string,
  options: TimeOptions = {},
): string => {
  if (!date) {
    return '';
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  if (formatStr) {
    return format(dateObj, formatStr);
  }
  const { showTimezone = false, timezone = null } = {
    ...defaultTimezoneOptions,
    ...options,
  };
  return new Intl.DateTimeFormat('en-US', {
    ...DATE_FORMAT_OPTIONS,
    ...TIME_FORMAT_OPTIONS,
    timeZone: timezone || undefined,
    timeZoneName: showTimezone ? 'short' : undefined,
  }).format(dateObj);
};

export const formatTime = (
  date?: number | string | Date | null,
  formatStr?: string,
): string => {
  if (!date) {
    return '';
  }
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  if (formatStr) {
    return format(dateObj, formatStr);
  }
  return new Intl.DateTimeFormat('en-US', TIME_FORMAT_OPTIONS).format(dateObj);
};

export const formatDateToNow = (
  date?: number | string | Date | null,
  options?: {
    includeSeconds?: boolean;
    addSuffix?: boolean;
  },
) => {
  if (!date) {
    return '';
  }
  return formatDistanceToNow(new Date(date), options);
};
